<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <div
        ref="menu"
        class="el-menu"
        :class="menuClass"
        :style="{ height: computedHeight, minHeight: computedHeight }"
    >
        <ul
            ref="menubar"
            class="el-menu__bar"
            role="menubar"
            :style="{ height: computedHeight, minHeight: computedHeight }"
            @scroll="debouncedOnMenubarScroll"
        >
            <slot />
        </ul>

        <div
            v-if="mode === 'horizontal' && isOverflow"
            class="el-menu__controls"
            :class="{ 'is-no-gradient': hideControlGradient }"
            :style="{ height: computedHeight, minHeight: computedHeight }"
        >
            <a-button type="ghost" glyph="i-chevron-left--16" @click="scrollMenu(true)" />
            <div class="el-menu__controls-divider" />
            <a-button type="ghost" glyph="i-chevron-right--16" @click="scrollMenu(false)" />
        </div>
    </div>
</template>

<script setup>
import {
    ref, reactive, computed, watch, watchEffect, nextTick,
    onMounted, onBeforeUnmount, provide,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AButton from '@core/components/button/button.vue';
import { useDebouncedFn } from '@core/composables/useDebouncedFn.ts';
import { useResizeObserver } from '@core/composables/useResizeObserver';
import { useSmoothScroll } from '@core/composables/useSmoothScroll';
import '@core/styles/menu.pcss';

const props = defineProps({
    mode: { type: String, default: 'vertical' },
    type: { type: String, default: 'primary' },
    modelValue: { type: String, default: '' },
    defaultActive: { type: String, default: '' },
    defaultOpeneds: { type: Array, default: () => [] },
    collapse: Boolean,
    scrollIntoExpanded: Boolean,
    uniqueOpened: Boolean,
    useRouteMatching: Boolean,
    background: { type: String, default: '' },
    noBorderBottom: Boolean,
    hideAllBorders: Boolean,
    height: { type: String, default: '64px' },
});

const emit = defineEmits(['select', 'open', 'close', 'update:modelValue']);
const route = useRoute();
const router = useRouter();
const menu = ref(null);
const menubar = ref(null);

const activeIndex = ref(props.modelValue || props.defaultActive);
const openedMenus = ref([]);
const items = reactive({});
const submenus = reactive({});

const state = reactive({
    itemsList: [],
    contentWidth: 0,
    isOverflow: false,
    hideControlGradient: false,
    margin: 16,
});

const computedHeight = computed(() => (props.mode === 'horizontal' ? props.height : null));
const isOverflow = computed(() => state.isOverflow);
const hideControlGradient = computed(() => state.hideControlGradient);
const menuClass = computed(() => ({
    [`el-menu--${props.mode}`]: true,
    [`el-menu--${props.type}`]: true,
    [`is-${props.background}`]: Boolean(props.background),
    'el-menu--collapse': props.collapse,
    'el-menu--without-submenus': Object.keys(submenus).length === 0,
    'is-borderBottom': props.noBorderBottom || props.hideAllBorders,
    'is-hide-all-borders': props.hideAllBorders,
}));

const { debouncedFn: debouncedOnMenubarScroll, cancel: cancelMenubarScroll } = useDebouncedFn(() => {
    if (props.mode === 'horizontal') updateControlGradient();
}, 25);

useResizeObserver(menu, () => {
    if (props.mode === 'horizontal') updateControls();
});

const { smoothScrollTo } = useSmoothScroll();

function getActiveIndexFromRoute(currentRoute) {
    const { matched, path } = currentRoute;
    if (items[path]) return path;
    for (let i = matched.length - 1; i >= 0; i--) {
        const matchedPath = matched[i].path;
        const regex = matched[i].regex;
        for (const key in items) {
            if (regex.test(key)) return key;
        }
        if (items[matchedPath]) return matchedPath;
    }
    return null;
}

function updateActiveIndex(currentRoute) {
    if (!props.useRouteMatching) return;
    const newActive = getActiveIndexFromRoute(currentRoute);
    if (newActive && newActive !== activeIndex.value) {
        activeIndex.value = newActive;
        updateOpenedMenu();
        scrollToActiveItem();
    }
}

function openMenu(index, indexPath) {
    if (openedMenus.value.includes(index)) return;
    if (props.uniqueOpened) {
        openedMenus.value = indexPath.length <= 1
            ? []
            : openedMenus.value.filter(i => indexPath.includes(i));
    }
    openedMenus.value.push(index);
}

function closeMenu(index) {
    const i = openedMenus.value.indexOf(index);
    if (i !== -1) openedMenus.value.splice(i, 1);
}

function handleItemClick(item) {
    const { index, indexPath } = item;
    if (indexPath.length === 1) {
        for (const key of Object.keys(submenus)) closeMenu(key);
    }
    if (activeIndex.value !== index) {
        emit('update:modelValue', index);
        emit('select', index, indexPath);
        if (!props.modelValue && item.route) {
            router.push(item.route).catch(() => {});
        }
        activeIndex.value = index;
    }
}

function handleSubmenuClick(submenu) {
    const { index, indexPath, $el, slots } = submenu;
    const isOpened = openedMenus.value.includes(index);
    if (isOpened && props.type !== 'primary') {
        closeMenu(index);
        emit('close', index, indexPath);
    } else {
        openMenu(index, indexPath);
        emit('open', index, indexPath);
        if (props.type === 'primary') {
            const defaultSlot = slots?.default?.() || [];
            const firstItem = defaultSlot.find(v => v?.type?.name === 'ElMenuItem');
            if (firstItem?.props?.index && items[firstItem.props.index]) {
                activeIndex.value = firstItem.props.index;
            }
        }
        if (props.scrollIntoExpanded) nextTick(() => scrollToSubmenu($el));
    }
}

function scrollToSubmenu(el) {
    const wrap = menu.value?.parentElement?.querySelector('.el-scrollbar__wrap');
    if (!wrap || !el) return;
    const top = el.getBoundingClientRect().top - menu.value.getBoundingClientRect().top;
    const bottom = top + el.offsetHeight;
    const viewTop = wrap.scrollTop;
    const viewBottom = viewTop + wrap.clientHeight;
    if (el.offsetHeight > wrap.clientHeight || top < viewTop) wrap.scrollTop = top;
    else if (bottom > viewBottom) wrap.scrollTop = bottom - wrap.clientHeight;
}

function scrollToActiveItem() {
    const item = items[activeIndex.value];
    if (item && props.mode === 'vertical') {
        nextTick(() => scrollToSubmenu(item.$el));
    }
}

function updateControls() {
    const list = Array.from(menubar.value?.children || []);
    state.itemsList = list;
    if (!list.length) return;
    const last = list[list.length - 1];
    state.contentWidth = last.offsetLeft + last.offsetWidth + state.margin;
    state.isOverflow = state.contentWidth + 2 * state.margin > menu.value.offsetWidth;
}

function updateControlGradient() {
    const last = state.itemsList[state.itemsList.length - 1];
    const rightOffset = last.getBoundingClientRect().right - menubar.value.getBoundingClientRect().right;
    state.hideControlGradient = rightOffset < 48;
}

function getScrollItem(dir) {
    const bounds = menubar.value.getBoundingClientRect();
    if (dir === 'right') {
        return state.itemsList.find(item => item.getBoundingClientRect().right > bounds.right);
    }
    if (dir === 'left') {
        for (let i = state.itemsList.length - 2; i >= 0; i--) {
            const left = state.itemsList[i].getBoundingClientRect().left;
            if (left < bounds.left) return state.itemsList[i + 1];
        }
        return state.itemsList[0];
    }
}

function scrollMenu(reverse) {
    const current = menubar.value.scrollLeft;
    const target = reverse ? getScrollItem('left') : getScrollItem('right');
    if (target) {
        const offset = target.getBoundingClientRect().left - menubar.value.getBoundingClientRect().left;
        smoothScrollTo(menubar.value, 'horizontal', current + offset, 300);
    }
}

function updateOpenedMenu() {
    const item = items[activeIndex.value];
    if (!item || props.mode === 'horizontal') return;
    nextTick(() => {
        (item.indexPath || []).forEach(i => {
            if (submenus[i] && !openedMenus.value.includes(i)) {
                openMenu(i, submenus[i].indexPath);
            }
        });
    });
}

onMounted(() => {
    if (props.useRouteMatching) {
        activeIndex.value = getActiveIndexFromRoute(route);
    }
    emit('select', activeIndex.value);
    nextTick(() => {
        updateOpenedMenu();
        scrollToActiveItem();
        if (props.mode === 'horizontal') updateControls();
    });
});

onBeforeUnmount(cancelMenubarScroll);

watch(() => props.modelValue, val => {
    activeIndex.value = val;
    updateOpenedMenu();
}, { immediate: true });

watch(() => route, updateActiveIndex, { immediate: true });

watch(() => Object.keys(items), () => {
    if (activeIndex.value && items[activeIndex.value]) updateOpenedMenu();
});

watchEffect(() => {
    if (props.defaultOpeneds.length && Object.keys(submenus).length >= props.defaultOpeneds.length && openedMenus.value.length === 0) {
        openedMenus.value = [...props.defaultOpeneds];
    }
});

provide('menuContext', {
    type: props.type,
    mode: computed(() => props.mode),
    collapse: computed(() => props.collapse),
    activeIndex,
    openedMenus,
    items,
    submenus,
    addItem: (item) => items[item.index] = item,
    removeItem: (item) => delete items[item.index],
    addSubmenu: (item) => submenus[item.index] = item,
    removeSubmenu: (item) => delete submenus[item.index],
    openMenu,
    closeMenu,
    handleItemClick,
    handleSubmenuClick,
});
</script>
